import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import produce from "immer";
import { loader } from "graphql.macro";
import { useQuery } from "react-apollo";
import cuid from "cuid";
import shallow from "zustand/shallow";
import { HeartSmallSvg, ArrowSmallSvg, PlusSvg, KlarnaSvg } from "@assets/js/icons";
import { log, placeholderReplace } from "@assets/js/utils";
import dataLayerPush, { dataLayerCommercePush, productSignature } from "@assets/js/gtm";

import classnames from "classnames";

import PDPModelSelector from "../PDPModelSelector";
import PDPHeroSelection from "../PDPHeroSelection";
import PDPMaterialSelector from "../PDPMaterialSelector";
import PDPMaterialSetSelector from "../PDPMaterialSetSelector";
import PDPMicroSwatches from "../PDPMicroSwatches";

import QuantitySelector from "../../checkout/QuantitySelector";

import { renderedImagePath } from "../../ProductImage";
import ActionButton from "../../ActionButton";
import ProductPrice from "../../ProductPrice";

import usePDPStore, {
  useProductSpecificationsOpen,
  useBasicConfiguration,
  useConfiguratorMode,
  useCustomConfiguration,
  useActiveProductData,
  useActiveModelData,
  usePendingProductData,
  useModelTypes,
  useActiveModelTypeIndex,
  useMaterialSets,
  useActiveMaterialIndex,
  useActiveMaterialSetIndex,
  useActiveMaterialSetMaterialIndex,
  useActiveMaterialIsUnallowed,
  useActiveSelector,
  useActiveUniqueMaterial,
  useConfigurationType,
  useConfiguringPendingProductData,
  useUniqueMaterialIsUnavailable,
  useResolvedPrice,
  isPreferedConfiguration,
  isHeroSelection
} from "@store/pdp";

import useGlobalStore, {
  useMarketInfo,
  useCommerceMarket,
  useCommerceMarketSupportsEcom,
  useCurrentSiteMode,
} from "@store/global";

import useBasketStore from "@store/basket";
import { useSubmissionMode, useSubmissionContent } from "@store/portfolio";
import useHeaderStore from "@store/header";
import useNotificationStore from "@store/notifications";
import useStockStore from "@store/stock";

import {
  OVERLAY,
  PORTFOLIO_TYPE,
  SITE_MODE,
  BUTTONSTYLE,
  CONFIGURATOR_MODE,
  CONFIGURATOR_SELECTOR,
  CONFIGURATOR_TYPE,
  DEFAULT_TRANSITION,
  NOTIFICATION,
  DATALAYER_EVENTS,
  INTERNATIONAL
} from "@constants";

import onMediaChanged from "@assets/js/on-media-changed";
import getViewportSize from "@assets/js/get-viewport-size";
import ViewportSizes from "@assets/js/viewport-sizes";
import getFormattedPrice from "@assets/js/get-formatted-price";
import showDeliveryCostsNotice from "@assets/js/showDeliveryCostsNotice";
import showKlarnaNotification from "@assets/js/showKlarnaNotification";

import "./pdp-configurator.scss";
import PDPCurrentConfigurationInfo from "../PDPCurrentConfigurationInfo";

const FetchStock = ({ children }) => {
  const activeConfigurationSKU = usePDPStore(
    (state) => state.activeConfigurationSKU
  );
  const stockList = useStockStore((state) => state.stockList);
  const fetchStockData = useStockStore((state) => state.fetchStockData);
  const [commerceMarket] = useCommerceMarket();
  useEffect(() => {
    if (
      !stockList[activeConfigurationSKU] &&
      activeConfigurationSKU &&
      commerceMarket
    ) {
      fetchStockData(commerceMarket, activeConfigurationSKU);
    }
  }, [commerceMarket, activeConfigurationSKU, stockList, fetchStockData]);
  return children;
}

const storeLocatorQuery = loader("./storeLocatorQuery.graphql");

const PDPConfigurator = ({
  children,
  allowOrder,
  currency,
  PDPNotOnEcomMarket,
  deliveryCostsNoticeDisabled,
  deliveryCostsNoticeLinkUrl,
  deliveryCostsNoticeLinkDescription,
  deliveryCostsNoticeThreshold,
  klarnaMarkets,
  klarnaMarketThreshold,
  klarnaScripts
}) => {
  const { t } = useTranslation();
  const texts = {
    loading: t("FritzHansen_Project_ConsumersAndProfessionals_General_Loading"),
    moreInfo: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_MoreInfo"),
    apply: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Apply"),
    continue: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Continue"),
    cancel: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_Cancel"),
    addToBasket: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_AddToBasket"
    ),
    addingToBasket: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_AddingToBasket"
    ),
    addedToBasket: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_AddedToBasket"
    ),
    notifyMe: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_NotifyMe"),
    notifyMeMessage: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_NotifyMeMessage"
    ),
    saveToProjects: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_SaveToProjects"
    ),
    inclVat: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_IncludeVat"),
    findInStore: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_FindInStore"
    ),
    errorTitle: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_NoConfigErrorTitle"
    ),
    errorText: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_NoConfigErrorText"
    ),
    addToWishList: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_AddToWishlist"
    ),
    configuratorResetWarning: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_ConfiguratorResetWarning"
    ),
    // VATIncluded: "Incl. {vatRate}% VAT",
    VATIncluded: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_VATIncluded"
    ),
    VATExcluded: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_VATExcluded"
    ),
    designerText: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DesignerText"
    ),
    notAllowedOrder: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_NotAllowedOrder"
    ),
    klarnaNotification: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_KlarnaNotification")
  };

  const [activeConfigurationSKU, fullFriendlySKU] = usePDPStore(
    (state) => [state.activeConfigurationSKU, state.fullFriendlySKU],
    shallow
  );
  const setActiveNotification = useNotificationStore(
    (state) => state.setActiveNotification
  );
  const [currentSiteMode] = useCurrentSiteMode();
  const history = useHistory();

  const [, ,] = useBasicConfiguration();

  const [, , fetchCustomConfiguration] = useCustomConfiguration();

  const [configuratorMode] = useConfiguratorMode();

  const [activeModelData] = useActiveModelData();
  const [activeProductData, setActiveProductData] = useActiveProductData();
  const [pendingProductData, setPendingProductData] = usePendingProductData();
  const [configuringPendingProductData, setConfiguringPendingProductData] = useConfiguringPendingProductData();
  const [resolvedPrice, setResolvedPrice] = useResolvedPrice();
  const [marketInfo] = useMarketInfo();

  const [modelTypes, setModelTypes] = useModelTypes();
  const [activeModelTypeIndex, setActiveModelTypeIndex] =
    useActiveModelTypeIndex();

  const fetchProductImageData = usePDPStore(
    (state) => state.fetchProductImageData
  );

  const fetchPendingConfiguration = usePDPStore((state) => state.fetchPendingConfiguration);
  const setAvailableStockExceeded = useBasketStore((store) => store.setAvailableStockExceeded);
  const setAvailableStockQuantity = useBasketStore((store) => store.setAvailableStockQuantity);
  const addProductToBasket = useBasketStore((state) => state.addProduct);
  const basketLineItems = useBasketStore((state) => state.lineItems);
  const basketPriceView = useBasketStore((state) => state.priceView);

  const [materialSets, setMaterialSets] = useMaterialSets();
  const [activeSelector, setActiveSelector] = useActiveSelector();
  const [activeMaterialIndex, setActiveMaterialIndex] =
    useActiveMaterialIndex();

  //position of chosen material set in active material bracket's list of material set
  const [activeMaterialSetIndex, setActiveMaterialSetIndex] =
    useActiveMaterialSetIndex();

  //position of chosen material in chosen material set's list of materials
  const [activeMaterialSetMaterialIndex, setActiveMaterialSetMaterialIndex] =
    useActiveMaterialSetMaterialIndex();

  //position of chosen material in chosen material set's list of materials
  const [activeMaterialIsUnallowed, setActiveMaterialIsUnallowed] =
    useActiveMaterialIsUnallowed();

  //Unique material selection is active, eg. specific table top on PK54
  const [activeUniqueMaterial, setActiveUniqueMaterial] = useActiveUniqueMaterial();

  const [uniqueMaterialIsUnavailable, setUniqueMaterialIsUnavailable] =
    useUniqueMaterialIsUnavailable();

  const [, setProductSpecificationsOpen] = useProductSpecificationsOpen();

  const [, setSubmissionMode] = useSubmissionMode();
  const [, setSubmissionContent] = useSubmissionContent();
  const setMenuOverlayActive = useHeaderStore(
    (store) => store.setMenuOverlayActive
  );

  const [waitingForBasketAdd, setWaitingForBasketAdd] = useState(false);

  const [mode, sc_lang] = useGlobalStore(
    (state) => [state.currentSiteMode, state.sc_lang],
    shallow
  );

  // const [currentMarket] = useCurrentMarket();
  const [commerceMarket] = useCommerceMarket();

  const [commerceMarketSupportsEcom] = useCommerceMarketSupportsEcom();

  const stockList = useStockStore((state) => state.stockList);
  // const fetchStockData = useStockStore((state) => state.fetchStockData);

  const [configurationType] = useConfigurationType();

  const [quantity, setQuantity] = useState(1);
  const [priceWithoutDiscount, setPriceWithoutDiscount] = useState(0);

  useEffect(() => {
    setUniqueMaterialIsUnavailable(false);
  }, [history, setUniqueMaterialIsUnavailable])

  useEffect(() => {
    //reset price logic on new configuration
    setPriceHasResolved(false);
    setZeroPriceForceNotifyMe(false);
    setPriceWithoutDiscount(0);
    setQuantity(activeProductData.modelTypeMinimumQuantity);
  }, [activeConfigurationSKU]);

  const menuId =
    mode === SITE_MODE.RESIDENTIAL
      ? process.env.REACT_APP_RESIDENTIAL_DESKTOP_ID
      : process.env.REACT_APP_PROFESSIONAL_DESKTOP_ID;

  const { data } = useQuery(storeLocatorQuery, {
    variables: { menuId },
    errorPolicy: "all",
  });

  // If just 1 materialset, show materials selector
  useEffect(() => {
    if (materialSets.length === 1) {
      setActiveSelector(CONFIGURATOR_SELECTOR.MATERIALS);
      setActiveMaterialSetIndex(0);
    }
  }, [materialSets, setActiveMaterialSetIndex, setActiveSelector]);

  const [currentViewport, setCurrentViewport] = useState(getViewportSize());

  onMediaChanged((newViewport) => {
    setCurrentViewport(newViewport);
  });

  useEffect(() => {
    const mainHeaderEl = document.querySelector(".main-header");
    const htmlEl = document.querySelector("html");

    if (activeSelector !== CONFIGURATOR_SELECTOR.NONE) {
      if (
        currentViewport?.name === ViewportSizes.SMALL.name ||
        currentViewport?.name === ViewportSizes.MEDIUM.name
      ) {
        if (mainHeaderEl) mainHeaderEl.classList.add("is-configuring");
        if (htmlEl) htmlEl.classList.add("is-configuring");

        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      if (
        currentViewport?.name === ViewportSizes.SMALL.name ||
        currentViewport?.name === ViewportSizes.MEDIUM.name) {
        if (mainHeaderEl) mainHeaderEl.classList.remove("is-configuring");
        if (htmlEl) htmlEl.classList.remove("is-configuring");
      }
    }
  }, [activeSelector, currentViewport?.name]);

  //animation presets
  const selectorAnimationVariants = {
    initial: {
      x: "30px",
      opacity: 0,
    },
    animate: {
      x: "0",
      opacity: 1,
      transition: DEFAULT_TRANSITION,
    },
    exit: {
      x: "-30px",
      opacity: 0,
    },
  };

  const [marketRemark, setMarketRemark] = useState(null);

  useEffect(() => {
    const getCommerceMarketRemark = () => {
      let currentMarket = marketInfo.markets.values.find(
        (item) => item.commerceMarket.value.toLowerCase() === commerceMarket
      );
      return currentMarket?.remark?.value;
    };
    if (marketInfo && commerceMarket) {
      setMarketRemark(getCommerceMarketRemark());
    }
  }, [marketInfo, commerceMarket]);

  useEffect(() => {
    const LoadKlarnaScript = () => {
      let existingElement = document.getElementById("klarna");
      if(existingElement){
        document.body.removeChild(existingElement);
      }

      let klarnaMarketData = klarnaScripts && klarnaScripts[commerceMarket];
      if(klarnaMarketData){
        const externalScript = document.createElement("script");
        externalScript.id = "klarna";
        externalScript.async = true;
        externalScript.type = "text/javascript";
        externalScript.setAttribute("data-environment", klarnaMarketData["environment"]);
        externalScript.setAttribute("data-client-id", klarnaMarketData["clientId"]);
        document.body.appendChild(externalScript);
        externalScript.src = klarnaMarketData["src"];
      }
    };
    if(commerceMarket){
      LoadKlarnaScript();
    }
  }, [commerceMarket]);

  useEffect(() => {
    if (resolvedPrice && window?.Klarna?.OnsiteMessaging) {
      window.Klarna.OnsiteMessaging.refresh()
    }
  }, [resolvedPrice]);

  const [localVATText, setLocalVATText] = useState(null);

  // ================================================================================================
  // ==   S E L E C T O R    A C T I O N S   ========================================================
  // ================================================================================================

  // ACTIVATE MODEL, MATERIAL SET OR MATERIAL
  const modelTypeChosenHandler = (item) => {
    const locatedModelIndex = modelTypes.indexOf(
      modelTypes.find((type) => item.typeId === type.typeId)
    );
    setActiveModelTypeIndex(locatedModelIndex);

    const newModelTypes = produce(modelTypes, (tmpTypes) => {
      tmpTypes.forEach((type) => {
        if (tmpTypes.indexOf(type) !== locatedModelIndex) {
          type.isActive = false;
        }
      });
      if (locatedModelIndex > -1) {
        tmpTypes[locatedModelIndex].isActive = true;
      }
    });
    setModelTypes(newModelTypes);

    var newPendingProductData = produce(pendingProductData, (tmpModel) => {
      tmpModel["model"] = {
        typeId: item.typeId,
        groupId: item.groupId,
        label: item.label,
        imageUrl: item.imageUrl
        // UseImageCDNUrl : isHeroSelection(activeConfigurationSKU,productData.heroselectionIds) || isPreferedConfiguration(skuStr,productData.MTPreferredConfigurationId)
      };
      tmpModel["materials"] = item.materials;
    });
    var skuStr = newPendingProductData.model.typeId;
    newPendingProductData.materials.forEach(element => {
      skuStr += "-" + element.activeMaterial.materialId;
    });

    newPendingProductData = {
      ...newPendingProductData,
      UseImageCDNUrl: isHeroSelection(skuStr, pendingProductData.heroselectionIds) || isPreferedConfiguration(skuStr, pendingProductData.MTPreferredConfigurationId)
    }

    fetchProductImageData(item.typeId);
    setConfiguringPendingProductData(newPendingProductData);
    setActiveUniqueMaterial(undefined);
  };

  // ================================================================================================

  const materialSetChosenHandler = (item) => {
    const locatedSetIndex = materialSets.indexOf(
      materialSets.find((set) => item.materialSetId === set.materialSetId)
    );

    const newMaterialSets = produce(materialSets, (tmpSets) => {
      tmpSets.forEach((set) => {
        //necessary for null check!
        if (set) {
          if (tmpSets.indexOf(set) !== locatedSetIndex) {
            set.isActive = false;
          }
        }
      });
      if (locatedSetIndex > -1) {
        tmpSets[locatedSetIndex].isActive = true;
      }
    });

    setMaterialSets(newMaterialSets);
    setActiveMaterialSetIndex(locatedSetIndex);
    setActiveSelector(CONFIGURATOR_SELECTOR.MATERIALS);
  };

  // ================================================================================================

  const materialChosenHandler = (item, isAllowed, uniqueMaterial) => {
    //log("MAT CHOSEN isAllowed?", isAllowed);
    // 1. get and save material position in active materialset's materials array
    const materialSetMaterialIndex = materialSets[
      activeMaterialSetIndex
    ].materials.indexOf(
      materialSets[activeMaterialSetIndex].materials.find(
        (material) => item.materialId === material.materialId
      )
    );
    setActiveMaterialSetMaterialIndex(materialSetMaterialIndex);

    // 2. prepare materials sets for state change
    const newMaterialSets = produce(materialSets, (tmpSets) => {
      //save materials list for later
      const currentMaterialSetMaterials =
        tmpSets[activeMaterialSetIndex].materials;

      currentMaterialSetMaterials.forEach((material) => {
        if (material.materialId !== item.materialId) material.isActive = false;
        else material.isActive = true;
      });
    });
    setMaterialSets(newMaterialSets);

    const materialSetPositionContainsUniqueMaterials = !!newMaterialSets.find(m => m.uniqueMaterials);

    setUniqueMaterialIsUnavailable(false);

    var newPendingProductData = produce(pendingProductData, (tmpModel) => {
      tmpModel.materials[activeMaterialIndex].activeMaterial = {
        materialId: item.materialId,
        materialLabel: item.materialLabel,
        materialSetId: item.materialSetId,
        materialSetLabel: item.materialSetLabel,
        imageUrl: uniqueMaterial ? uniqueMaterial.imageUrl : item.imageUrl,
        uniqueMaterial: uniqueMaterial ? uniqueMaterial : undefined
      };
    });
    var skuStr = newPendingProductData.model.typeId;
    newPendingProductData.materials.forEach(element => {
      skuStr += "-" + element.activeMaterial.materialId;
    });

    newPendingProductData = produce(newPendingProductData, (tmpModel) => {
      tmpModel.model.configurationId = skuStr;
    });

    newPendingProductData = {
      ...newPendingProductData,
      UseImageCDNUrl: isHeroSelection(skuStr, pendingProductData.heroselectionIds) || isPreferedConfiguration(skuStr, pendingProductData.MTPreferredConfigurationId)
    }

    setConfiguringPendingProductData(newPendingProductData);
    setActiveMaterialIsUnallowed(isAllowed);

    if (uniqueMaterial) {
      setActiveUniqueMaterial(uniqueMaterial);
    }
    else if (materialSetPositionContainsUniqueMaterials) {
      // the current selected material is not unique, so it clears the previousl selected uniqueMaterial
      setActiveUniqueMaterial(undefined);
    }
  };

  // ================================================================================================
  // ==   F O O T E R    A C T I O N S   ============================================================
  // ================================================================================================

  const saveToProjectsHandler = () => {
    openPortfolioOverlay();
  };

  const wishListHandler = () => {
    openPortfolioOverlay();
  };

  const productAddedErrorHandler = (showQuantityError) => {
    setWaitingForBasketAdd(false);
    setQuantity(activeProductData.modelTypeMinimumQuantity);

    if (showQuantityError) {
      log("SHOW QUANTITY ERROR, ADD");
    }
  };

  const productAddedSuccessHandler = (uniqueProductIsAdded) => {
    setWaitingForBasketAdd(false);
    setQuantity(activeProductData.modelTypeMinimumQuantity);

    setActiveNotification({
      id: cuid(),
      title: t("FritzHansen_Project_ConsumersAndProfessionals_Notifications_AddToBasketTitle"),
      bodyText: uniqueProductIsAdded ? t("FritzHansen_Feature_Products_Configurator_Basket_UniqueMaterial1") : null,
      bodyText2: uniqueProductIsAdded ? t("FritzHansen_Feature_Products_Configurator_Basket_UniqueMaterial2") : null,
      buttonText: t(
        "FritzHansen_Project_ConsumersAndProfessionals_Cart_OpenBasket"
      ),
      type: NOTIFICATION.ITEM_ADDED,
      clickHandler: () => {
        setMenuOverlayActive(OVERLAY.BASKET);
      },
      timer: uniqueProductIsAdded ? 10000 : 2000,
    });
    if (uniqueProductIsAdded) {
      setUniqueMaterialIsUnavailable(true);
    }

    // GTM Tracking
    dataLayerCommercePush("addToCart", {
      currencyCode: currency,
      add: {
        products: [{
          ...productSignature(activeProductData, resolvedPrice?.unitPrice), quantity: quantity ?? 1
        }
        ]
      }
    });
  };

  const [priceHasResolved, setPriceHasResolved] = useState(false);
  const [zeroPriceForceNotifyMe, setZeroPriceForceNotifyMe] = useState(false);
  const gtmRegisteredSKU = useRef({ activeConfigurationSKU });

  const updateButtonsAccordingToPrice = (price, configurationId) => {
    //check if located price is zero, then resort to notify me

    setResolvedPrice(price);
    setPriceHasResolved(true);

    if (!isNaN(price?.unitPrice) && price?.unitPrice === 0) {
      setZeroPriceForceNotifyMe(true);
    }

    if (!gtmRegisteredSKU?.current || gtmRegisteredSKU?.current !== configurationId) {
      dataLayerCommercePush("productDetail", {
        currencyCode: currency,
        detail: {
          products: [
            productSignature(activeProductData, price?.unitPrice)
          ]
        },
      });
      gtmRegisteredSKU.current = activeConfigurationSKU;
    }
  };


  const showFooterLoader = () => {
    //test if an intermittent loader state is needed

    let result = (
      !activeModelData ||
      !commerceMarket ||
      !priceHasResolved
    );

    log("showFooterLoader | result=" + result);

    return result;
  };

  const showBasketButton = () => {
    //only show basket button if theres product data, market supports ecom, orders are allowed, and the price is != zero
    return (
      activeModelData &&
      commerceMarket &&
      commerceMarketSupportsEcom &&
      allowOrder &&
      priceHasResolved &&
      !zeroPriceForceNotifyMe
    );
  };

  const showNotInMarketText = () => {
    //only show basket button if theres product data, market supports ecom, orders are allowed, and the price is != zero
    var isMarketText = PDPNotOnEcomMarket !== "";
    return (activeModelData && !commerceMarketSupportsEcom && isMarketText)
  };

  const showNotAllowedOrderText = () => {
    return (activeProductData && commerceMarketSupportsEcom && !activeProductData?.allowOrderMarkets?.includes(commerceMarket));
  };

  const showNotifyMeButton = () => {
    let isSkuProduct = configuratorMode === CONFIGURATOR_MODE.NONE;
    let skuProductHasStock = false;

    if (stockList && stockList[activeConfigurationSKU] && isSkuProduct) {
      skuProductHasStock = stockList[activeConfigurationSKU]?.Stock > 0;
    }

    //product is sku product but out of stock
    let caseSkuProductNoStock =
      activeModelData &&
      commerceMarket &&
      commerceMarketSupportsEcom &&
      isSkuProduct &&
      !skuProductHasStock &&
      allowOrder;

    //product is sku product and has stock
    let caseSkuProductHasStock =
      activeModelData &&
      commerceMarket &&
      commerceMarketSupportsEcom &&
      isSkuProduct &&
      skuProductHasStock &&
      !allowOrder;

    //product is configurable product but allow order is disabled
    let caseConfigProductNoOrder =
      activeModelData &&
      commerceMarket &&
      commerceMarketSupportsEcom &&
      !isSkuProduct &&
      !allowOrder;

    //price is zero, so use notify me
    let caseZeroPriceAndEcomSupport =
      activeModelData &&
      commerceMarket &&
      commerceMarketSupportsEcom &&
      priceHasResolved &&
      zeroPriceForceNotifyMe;

    log(
      "PDP | showNotifyMeButton | caseSkuProductNoStock=" +
      caseSkuProductNoStock
    );
    log(
      "PDP | showNotifyMeButton | caseSkuProductHasStock=" +
      caseSkuProductHasStock
    );
    log(
      "PDP | showNotifyMeButton | caseConfigProductNoOrder=" +
      caseConfigProductNoOrder
    );
    log(
      "PDP | showNotifyMeButton | caseZeroPriceAndEcomSupport=" +
      caseConfigProductNoOrder
    );

    if (isSkuProduct) {
      return (
        caseSkuProductNoStock ||
        caseSkuProductHasStock ||
        caseZeroPriceAndEcomSupport
      );
    } else {
      return caseConfigProductNoOrder || caseZeroPriceAndEcomSupport;
    }
  };

  const onAddToBasket = () => {
    if (waitingForBasketAdd) return false;

    setWaitingForBasketAdd(true);

    if (maxQuantity().reachedMax) {
      setAvailableStockExceeded(true);
      setWaitingForBasketAdd(false);
      setAvailableStockQuantity(maxQuantity().totalStock);
      return false;
    }

    let fullFriendlySKUWithOptions = fullFriendlySKU;
    if(activeProductData && activeProductData.options && activeProductData.options.length > 0){
      fullFriendlySKUWithOptions += "_";
      for(var i = 0; i < activeProductData.options.length; i++){
        fullFriendlySKUWithOptions += activeProductData.options[i].activeOptionId + "-";
      }
      fullFriendlySKUWithOptions = fullFriendlySKUWithOptions.slice(0, -1);
    }

    addProductToBasket(
      {
        productId: fullFriendlySKUWithOptions,
        uniqueId: activeUniqueMaterial ? activeUniqueMaterial.id : "",
        quantity: quantity
      },
      productAddedSuccessHandler,
      productAddedErrorHandler
    );
  };

  const onNotifyMe = () => {
    setMenuOverlayActive(OVERLAY.NOTIFY_ME);
  };

  const openPortfolioOverlay = () => {
    let content = {
      imageSrcHigh:
        configuratorMode === CONFIGURATOR_MODE.NONE
          ? activeProductData.imageUrl
          : renderedImagePath({
            imageId: activeConfigurationSKU,
            sw: 300,
          }),
      imageSrcLow:
        configuratorMode === CONFIGURATOR_MODE.NONE
          ? activeProductData.imageUrl
          : renderedImagePath({
            imageId: activeConfigurationSKU,
            sw: 50,
          }),
      imageWidth: 300,
      imageHeight: 300,

      portfolioItem: {
        productId: activeConfigurationSKU,
        modelTypeId: activeProductData.model.typeId,
        quantity: 1,
        isUniqueMaterial: !!activeUniqueMaterial
      },
    };

    setSubmissionContent(content);
    setSubmissionMode(PORTFOLIO_TYPE.PRODUCT);
    setMenuOverlayActive(OVERLAY.PORTFOLIO_SUBMISSION);
  };

  const moreInfoHandler = () => {
    document.querySelector("#productSpecifications").scrollIntoView({
      behavior: "smooth",
    });
    setProductSpecificationsOpen(true);
  };

  const cancelOverviewHandler = () => {
    let params = {
      modelTypeId: activeProductData?.model?.typeId,
      market: commerceMarket,
    };

    let basicParams = {
      modelTypeId: activeProductData?.model?.typeId,
      market: commerceMarket,
    };

    if (activeMaterialIsUnallowed) {
      params[`material${activeMaterialIndex + 1}Id`] =
        activeProductData.materials[
          activeMaterialIndex
        ].activeMaterial.materialId;
    } else {
      activeProductData.materials.forEach((_item, index) => {
        params[`material${index + 1}Id`] =
          activeProductData.materials[index].activeMaterial.materialId;
      });
    }
    if (activeProductData?.model?.configurationId !== pendingProductData?.model?.configurationId) {
      fetchPendingConfiguration(params, basicParams, sc_lang, activeProductData);
      fetchProductImageData(activeProductData.model.typeId);
    }
    resetConfigurator();
  }

  const cancelModelHandler = () => {
    setConfiguringPendingProductData(pendingProductData);
    fetchProductImageData(pendingProductData.model.typeId);
    resetConfigurator();
    setActiveSelector(CONFIGURATOR_SELECTOR.OVERVIEW);
  };

  const submitModelTypeHandler = () => {
    setActiveSelector(CONFIGURATOR_SELECTOR.OVERVIEW);

    let params = {
      modelTypeId: configuringPendingProductData?.model?.typeId,
      market: commerceMarket,
    };

    let basicParams = {
      modelTypeId: configuringPendingProductData?.model?.typeId,
      market: commerceMarket,
    };

    let skuStr = "NOSKU";
    if (configuringPendingProductData && configuringPendingProductData.model) skuStr = configuringPendingProductData.model.typeId;
    if (configuringPendingProductData && configuringPendingProductData.materials) {
      for (let i = 0; i < configuringPendingProductData.materials.length; i++) {
        skuStr += "-" + configuringPendingProductData.materials[i].activeMaterial.materialId;
      }
      configuringPendingProductData.materials.forEach((_item, index) => {
        params[`material${index + 1}Id`] =
          configuringPendingProductData.materials[index].activeMaterial.materialId;
      });
    }

    if (skuStr !== "NOSKU") {
      let newPendingProductData = produce(configuringPendingProductData, (tmpModel) => {
        tmpModel.model.configurationId = skuStr;
      });
      fetchPendingConfiguration(params, basicParams, sc_lang, newPendingProductData);
    }
  };

  const getCurrentMarketText = () => {
    var market = marketInfo?.markets?.values?.find(
      (item) => item.commerceMarket.value.toLowerCase() === commerceMarket
    );
    return market?.countryName?.value;
  };

  const confirmConfigurationHandler = () => {
    resetConfigurator();

    let params = {
      modelTypeId: pendingProductData?.model?.typeId,
      market: commerceMarket,
    };

    if(configurationType?.type?.value === CONFIGURATOR_TYPE.VARIATION_GROUP){
      params["variationGroupId"] = configurationType.id.value;
    }
    
    let options = [];
    pendingProductData.selectedOptions.forEach((option) => {
      options.push(option.split('|')[1]);
    });
    params["options"] = options;
    if (activeMaterialIsUnallowed) {
      params[`material${activeMaterialIndex + 1}Id`] =
        pendingProductData.materials[
          activeMaterialIndex
        ].activeMaterial.materialId;
    } else {
      pendingProductData.materials.forEach((_item, index) => {
        params[`material${index + 1}Id`] =
          pendingProductData.materials[index].activeMaterial.materialId;
      });
    }
    setActiveProductData(pendingProductData);
    if (activeProductData?.model?.configurationId !== pendingProductData?.model?.configurationId) {
      fetchCustomConfiguration(params, sc_lang);
    }

  }

  const cancelMaterialHandler = () => {
    if (activeMaterialSetMaterialIndex > -1) {
      if (activeProductData.materials[activeMaterialSetMaterialIndex]?.activeMaterial?.uniqueMaterial) {
        setActiveUniqueMaterial(activeProductData.materials[activeMaterialSetMaterialIndex].activeMaterial.uniqueMaterial)
      }
      else {
        setActiveUniqueMaterial(undefined)
      }
    }

    setConfiguringPendingProductData(pendingProductData);
    fetchProductImageData(pendingProductData.model.typeId);
    resetConfigurator();
    setActiveSelector(CONFIGURATOR_SELECTOR.OVERVIEW);
  };

  const submitMaterialHandler = () => {
    setPendingProductData(configuringPendingProductData);
    setActiveSelector(CONFIGURATOR_SELECTOR.OVERVIEW);

    dataLayerPush({
      event: "trackConfigurator"
    });
  };

  const getParamCount = () => {
    let p = activeProductData?.materials?.length;
    if (configuratorMode === CONFIGURATOR_MODE.ADVANCED) p++;

    return p;
  };

  const backToMaterialSetsHandler = () => {
    setActiveMaterialSetMaterialIndex(-1);
    setActiveMaterialIsUnallowed(false);
    setActiveSelector(CONFIGURATOR_SELECTOR.MATERIAL_SETS);
  };

  const resetConfigurator = () => {
    setModelTypes([]);
    setMaterialSets([]);
    setActiveModelTypeIndex(-1);
    setActiveMaterialIndex(-1);
    setActiveMaterialSetIndex(-1);
    setActiveMaterialSetMaterialIndex(-1);
    setActiveMaterialIsUnallowed(false);
    setActiveSelector(CONFIGURATOR_SELECTOR.NONE);
  };

  const maxQuantity = () => {
    if (activeUniqueMaterial) {
      return 1;
    }

    let isSkuProduct = configuratorMode === CONFIGURATOR_MODE.NONE;
    if (!isSkuProduct) {
      return 0;
    }
    var minimumQuantity = activeProductData.modelTypeMinimumQuantity;
    let stockQty = 0;
    let totalStock = 0;
    if (stockList && stockList[activeConfigurationSKU] && isSkuProduct) {
      stockQty = totalStock = stockList[activeConfigurationSKU]?.Stock ?? 0;
    }
    totalStock = totalStock - (totalStock % minimumQuantity);
    stockQty = stockQty - (stockQty % minimumQuantity);

    const lineItemWithCurrentConfiguration = basketLineItems?.find(l => l.FriendlyName === activeConfigurationSKU);
    if (lineItemWithCurrentConfiguration && stockQty > 0) {
      stockQty = stockQty - lineItemWithCurrentConfiguration.Quantity;
    }
    var reachedMax = false;
    if (stockQty < 1) {
      stockQty = 1;
      reachedMax = true;
    }


    return { stockQty: stockQty, totalStock: totalStock, reachedMax: reachedMax };
  }

  const changeQuantity = (value, oldValue = 0) => {
    setQuantity(value);
  };
  
  const locale = commerceMarket?.toLowerCase() === INTERNATIONAL ? process.env.REACT_APP_COMMERCE_FALLBACK_LOCALE :`en-${commerceMarket?.toUpperCase()}`;
  const getKlarnaPrice = () => {
    if (resolvedPrice?.unitPriceWithoutQuantity && commerceMarket) {
      let klarnaPrice = (resolvedPrice.unitPriceWithoutQuantity * quantity) * 100;
      return new Intl.NumberFormat(locale, { useGrouping: false }).format(klarnaPrice);
    }
    return 0;
  }

  // ================================================================================================
  // ==   R E N D E R   F U N C T I O N   ===========================================================
  // ================================================================================================

  const classList = classnames({
    "pdp-configurator": true,
    "pdp-configurator--1-params": getParamCount() === 1,
    "pdp-configurator--2-params": getParamCount() === 2,
    "pdp-configurator--3-params": getParamCount() === 3,
    "pdp-configurator--4-params": getParamCount() === 4,
    "pdp-configurator--5-params": getParamCount() === 5,
    "pdp-configurator--6-params": getParamCount() === 6,
    "pdp-configurator--7-params": getParamCount() === 7,
    "is-configuring": activeSelector !== CONFIGURATOR_SELECTOR.NONE,
  });

  const priceClassList = classnames({
    "pdp-price": true,
    "has-resolved": priceHasResolved,
  });

  return (<FetchStock>
    <div className={classList}>
      <div className="pdp-configurator__content">
        <div className="pdp-configurator__slides">
          {activeSelector === CONFIGURATOR_SELECTOR.NONE && (
            <AnimatePresence>
              <motion.div
                key="pdpConfigSlide0"
                initial={selectorAnimationVariants.initial}
                animate={selectorAnimationVariants.animate}
                exit={selectorAnimationVariants.exit}
                className="pdp-configurator__slide pdp-configurator__header"
              >
                <header className="pdp-configurator__header__text">
                  <h1>
                    {!activeProductData.title && texts.loading}
                    {activeProductData.title === "NONE"
                      ? texts.errorTitle
                      : activeProductData.title}
                  </h1>

                  <p>
                    {activeProductData.designerName && <>
                      {texts.designerText.replace('{designer}', activeProductData.designerName)}
                      {activeProductData.designYear && <span>, {activeProductData.designYear}</span>}
                    </>}
                  </p>
                  
                  {activeProductData.tags && activeProductData.tags.map((tag) => {
                    return <div className="pdp-configurator__header__tag">
                      {tag}
                    </div>
                  })}
                </header>

                {configuratorMode !== CONFIGURATOR_MODE.NONE && (
                  <>
                    {activeProductData.heroselectionIds && <PDPHeroSelection />}
                    <PDPCurrentConfigurationInfo />
                  </>
                )}
                {configuratorMode === CONFIGURATOR_MODE.NONE && <PDPMicroSwatches />}              
                <div className="pdp-configurator__header__price">
                  {currentSiteMode === SITE_MODE.RESIDENTIAL &&
                    (currentViewport?.name === ViewportSizes.SMALL.name ||
                      currentViewport?.name === ViewportSizes.MEDIUM.name) && (
                      <div className="quantity-selector-container">
                        <button className="basic-link" onClick={wishListHandler}>
                          <span className="icon">
                            <HeartSmallSvg
                              fillHex="transparent"
                              strokeHex="#000"
                            />
                          </span>
                          <span className="text">{texts.addToWishList}</span>
                        </button>
                        {showBasketButton() && !showNotifyMeButton() ?
                          <QuantitySelector
                            isLocked={waitingForBasketAdd}
                            defaultValue={quantity}
                            changeHandler={changeQuantity}
                            maxValue={maxQuantity().stockQty}
                            minValue={activeProductData.modelTypeMinimumQuantity}
                            showQuantityValidationModal={true}
                            step={activeProductData.modelTypeMinimumQuantity}
                            disableInputChange={true}
                            totalStock={maxQuantity().totalStock} /> : <div className="empty-quantity-mobile"></div>}
                      </div>
                    )}
                  {(currentViewport?.name !== ViewportSizes.SMALL.name && currentViewport?.name !== ViewportSizes.MEDIUM.name) &&
                    <>
                      {(showBasketButton() && !showNotifyMeButton()) ?
                        <QuantitySelector
                          isLocked={waitingForBasketAdd}
                          defaultValue={quantity}
                          changeHandler={changeQuantity}
                          maxValue={maxQuantity().stockQty}
                          minValue={activeProductData.modelTypeMinimumQuantity}
                          showQuantityValidationModal={true}
                          step={activeProductData.modelTypeMinimumQuantity}
                          disableInputChange={true}
                          totalStock={maxQuantity().totalStock} /> : <div className="empty-quantity"></div>}
                    </>}
                  {currentSiteMode === SITE_MODE.PROFESSIONAL && (
                    <>
                      {showFooterLoader() ? (<span className="loader-dots"><span></span></span>) : (
                        showNotifyMeButton() ? (
                          <button className="basic-link" onClick={onNotifyMe}>
                            <span className="icon">
                              <PlusSvg />
                            </span>
                            <span className="text">{texts.notifyMe}</span>
                          </button>
                        ) : (
                          showBasketButton() && (
                            <button
                              className={`basic-link${waitingForBasketAdd ? " is-disabled" : ""
                                } professional-add-to-cart`}
                              onClick={onAddToBasket}
                            >
                              <span className="icon">
                                <PlusSvg />
                              </span>
                              <span className="text">
                                {waitingForBasketAdd
                                  ? texts.addingToBasket
                                  : texts.addToBasket}
                              </span>
                            </button>
                          )
                        )
                      )}

                    </>
                  )}
                  {priceWithoutDiscount > 0 &&
                    <div className="pdp-configurator__header__discount-price-wrapper">
                      <h4 className="pdp-discount-price">
                        <span>{getFormattedPrice(sc_lang, commerceMarket, resolvedPrice?.currency, priceWithoutDiscount)}</span>
                      </h4>
                    </div>}
                  <div className="pdp-configurator__header__price-wrapper">
                    {priceWithoutDiscount > 0 && <h4 className="pdp-discount-price">
                        <div>
                          <span>{getFormattedPrice(sc_lang, commerceMarket, resolvedPrice?.currency, priceWithoutDiscount)}</span>
                        </div>
                      </h4>}
                    <h4 className={priceClassList}>
                      {marketRemark && <small>{marketRemark}</small>}
                      <ProductPrice
                        configurationId={activeConfigurationSKU}
                        taxonomies={activeProductData?.taxonomies}
                        onResolved={(price, configurationId, priceWithoutDiscount) => {
                          setPriceWithoutDiscount(priceWithoutDiscount)
                          updateButtonsAccordingToPrice(price, configurationId);
                          let vatRate = price?.vatRate ? price.vatRate * 100 : 0;
                          let placeholderText = vatRate > 0 ? texts.VATIncluded : texts.VATExcluded
                          let txt = placeholderReplace(placeholderText, { vatRate });
                          if (txt.length) setLocalVATText(txt);
                        }}
                      />
                      {showDeliveryCostsNotice(
                        deliveryCostsNoticeDisabled,
                        deliveryCostsNoticeLinkDescription,
                        deliveryCostsNoticeThreshold,
                        commerceMarket,
                        priceHasResolved,
                        resolvedPrice?.unitPriceWithoutQuantity,
                        quantity,
                        basketPriceView) &&
                        <a href={deliveryCostsNoticeLinkUrl} className="delivery-costs-notice">{deliveryCostsNoticeLinkDescription}</a>
                      }
                      {localVATText && resolvedPrice && <em>{localVATText}</em>}
                    </h4>
                    {/* {!showNotInMarketText() && !showNotAllowedOrderText() && !showNotifyMeButton() && showKlarnaNotification(
                      klarnaMarketThreshold,
                      klarnaMarkets,
                      commerceMarket,
                      commerceMarketSupportsEcom,
                      resolvedPrice?.unitPriceWithoutQuantity,
                      quantity) &&
                      <div className="klarna-notification">
                        <span>{texts.klarnaNotification}<KlarnaSvg /></span>
                      </div>}*/}
                  </div>
                </div>
                {!showNotInMarketText() && !showNotAllowedOrderText() && !showNotifyMeButton() && showKlarnaNotification(
                  klarnaMarketThreshold,
                  klarnaMarkets,
                  commerceMarket,
                  commerceMarketSupportsEcom,
                  resolvedPrice?.unitPriceWithoutQuantity,
                  quantity) && <div className="pdp-configurator__header__klarna">
                    <klarna-placement id="klarna-placement" data-key="credit-promotion-auto-size" data-locale={locale} data-purchase-amount={getKlarnaPrice()} />
                  </div>}
                <div className="pdp-configurator__header__cta">
                  {data?.menu?.storeLocatorLink?.jss?.value?.href && (
                    <ActionButton
                      //icon="MapMarkerSvg"
                      text={texts.findInStore.toUpperCase()}
                      buttonType={BUTTONSTYLE.GHOST}
                      customClass="action-button--secondary"
                      href={data.menu.storeLocatorLink.jss.value.href}
                      clickHandler={() => {
                        dataLayerPush({
                          event: DATALAYER_EVENTS.STORELOCATOR,
                          storeAction: 'find store',
                          storeReferrer: window.location.href,
                          storeProductName: `${activeProductData.dataLayerProductName}, ${activeProductData.dataLayerProductVariant}`,
                          storeProductBrand: activeProductData.dataLayerProductBrand,
                          storeProductCategory: activeProductData.dataLayerProductCategory
                        })
                      }}
                    />
                  )}
                  {currentSiteMode === SITE_MODE.PROFESSIONAL && (
                    <ActionButton
                      text={texts.saveToProjects.toUpperCase()}
                      buttonType={BUTTONSTYLE.SOLID}
                      clickHandler={saveToProjectsHandler}
                      isInverted={true}
                      customClass="action-button--primary"
                    />
                  )}
                  {currentSiteMode === SITE_MODE.RESIDENTIAL && (
                    <>
                      {showFooterLoader() && !showNotInMarketText() ? (<span className="loader-dots action-button"><span></span></span>) : (
                        showNotInMarketText() ? (
                          <div className="pdp-not-in-commercemarket-text" dangerouslySetInnerHTML={{ __html: PDPNotOnEcomMarket?.replace("{{currentMarket}}", getCurrentMarketText()) }}></div>
                        ) : (showNotAllowedOrderText() ? (<div className="pdp-not-in-commercemarket-text">{texts.notAllowedOrder}</div>) : (
                          (showNotifyMeButton() ? (
                            <ActionButton
                              isInverted={true}
                              text={texts.notifyMe.toUpperCase()}
                              buttonType={BUTTONSTYLE.SOLID}
                              customClass="action-button--primary"
                              clickHandler={onNotifyMe}
                            />
                          ) : (showBasketButton() && (
                            <ActionButton
                              text={
                                waitingForBasketAdd
                                  ? texts.addingToBasket.toUpperCase()
                                  : uniqueMaterialIsUnavailable ?
                                    texts.addedToBasket.toUpperCase() : texts.addToBasket.toUpperCase()
                              }
                              isInverted={true}
                              //icon="BasketSvg"
                              buttonType={BUTTONSTYLE.SOLID}
                              customClass="action-button--primary"
                              isDisabled={waitingForBasketAdd || uniqueMaterialIsUnavailable}
                              clickHandler={onAddToBasket}
                            />
                          )
                          ))
                        )
                        ))}
                    </>
                  )}
                </div>
              </motion.div>
              {showNotifyMeButton() && !showNotAllowedOrderText()? (
                <p className="pdp-configurator__notify-me-message">
                  {texts.notifyMeMessage}
                </p>
              ) : (
                <></>
              )}
            </AnimatePresence>
          )}
          {modelTypes &&
            modelTypes.length > 0 &&
            activeSelector === CONFIGURATOR_SELECTOR.MODEL_TYPES && (
              <AnimatePresence>
                <motion.div
                  key="pdpConfigSlide1"
                  initial={selectorAnimationVariants.initial}
                  animate={selectorAnimationVariants.animate}
                  exit={selectorAnimationVariants.exit}
                  className="pdp-configurator__slide"
                >
                  <PDPModelSelector
                    tiles={modelTypes}
                    itemClickHandler={modelTypeChosenHandler}
                    currentViewport={currentViewport}
                  />
                </motion.div>
              </AnimatePresence>
            )}
          {materialSets &&
            materialSets.length > 1 &&
            activeSelector === CONFIGURATOR_SELECTOR.MATERIAL_SETS && (
              <AnimatePresence>
                <motion.div
                  key="pdpConfigSlide2"
                  initial={selectorAnimationVariants.initial}
                  animate={selectorAnimationVariants.animate}
                  exit={selectorAnimationVariants.exit}
                  className="pdp-configurator__slide"
                >
                  <PDPMaterialSetSelector
                    materialSets={materialSets}
                    itemClickHandler={materialSetChosenHandler}
                    currentViewport={currentViewport}
                  />
                </motion.div>
              </AnimatePresence>
            )}
          {materialSets &&
            materialSets.length > 0 &&
            activeSelector === CONFIGURATOR_SELECTOR.MATERIALS && (
              <AnimatePresence>
                <motion.div
                  key="pdpConfigSlide3"
                  initial={selectorAnimationVariants.initial}
                  animate={selectorAnimationVariants.animate}
                  exit={selectorAnimationVariants.exit}
                  className="pdp-configurator__slide"
                >
                  <PDPMaterialSelector
                    materialSet={
                      activeMaterialSetIndex > -1 && materialSets
                        ? materialSets[activeMaterialSetIndex]
                        : {}
                    }
                    materials={
                      activeMaterialSetIndex > -1 && materialSets
                        ? materialSets[activeMaterialSetIndex].materials
                        : []
                    }
                    itemClickHandler={materialChosenHandler}
                    currentViewport={currentViewport}
                    hasHiddenConfigurationElement={activeProductData?.hasHiddenConfigurationElements}
                  />
                </motion.div>
              </AnimatePresence>
            )}
          {activeSelector === CONFIGURATOR_SELECTOR.OVERVIEW && (
            <AnimatePresence>
              <motion.div
                key="pdpConfigSlide4"
                initial={selectorAnimationVariants.initial}
                animate={selectorAnimationVariants.animate}
                exit={selectorAnimationVariants.exit}
                className="pdp-configurator__slide"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {activeSelector === CONFIGURATOR_SELECTOR.OVERVIEW && (
        <div className="pdp-configurator__footer">
          <div className="pdp-configurator__footer__cta">
            <button className="basic-link" onClick={cancelOverviewHandler}>
              <span className="text">{texts.cancel}</span>
            </button>

            <ActionButton
              text={"Confirm"}
              buttonType={BUTTONSTYLE.SOLID}
              customClass="action-button--primary"
              clickHandler={confirmConfigurationHandler}
              isInverted={true}
            />
          </div>
        </div>
      )}

      {activeSelector === CONFIGURATOR_SELECTOR.MODEL_TYPES && (
        <div className="pdp-configurator__footer">
          <div className="pdp-configurator__footer__cta">
            <button className="basic-link" onClick={cancelModelHandler}>
              <span className="text">{texts.cancel}</span>
            </button>

            <ActionButton
              text={texts.apply}
              buttonType={BUTTONSTYLE.SOLID}
              customClass="action-button--primary"
              isDisabled={activeModelTypeIndex < 0}
              clickHandler={submitModelTypeHandler}
              isInverted={true}
            />
          </div>
        </div>
      )}

      {(activeSelector === CONFIGURATOR_SELECTOR.MATERIAL_SETS ||
        activeSelector === CONFIGURATOR_SELECTOR.MATERIALS) && (
          <div className="pdp-configurator__footer">
            {(activeMaterialIsUnallowed && !activeProductData?.hasHiddenConfigurationElements) && <p>{texts.configuratorResetWarning}</p>}
            {activeSelector === CONFIGURATOR_SELECTOR.MATERIALS && materialSets.length > 1 ? (
              <ActionButton
                hasArrow={true}
                buttonType={BUTTONSTYLE.ICON_LINK}
                clickHandler={backToMaterialSetsHandler}
                customClass="pdp-configurator__back"
              />
            ) : (
              <ActionButton
                hasArrow={true}
                buttonType={BUTTONSTYLE.ICON_LINK}
                clickHandler={cancelMaterialHandler}
                customClass="pdp-configurator__back"
              />
            )}
            <div className="pdp-configurator__footer__cta">
              <ActionButton
                text={texts.apply}
                buttonType={BUTTONSTYLE.SOLID}
                customClass="action-button--primary"
                isDisabled={activeMaterialSetMaterialIndex < 0}
                clickHandler={submitMaterialHandler}
                isInverted={true}
              />
            </div>
          </div>
        )}
    </div>
  </FetchStock>
  );
};
PDPConfigurator.propTypes = {};

export default PDPConfigurator;
